import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lock, ShieldCheck, User, Users, Plus, Trash2, Star, KeyRound, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function ResetPasswordDialog({ admin, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const submit = async () => {
    try {
      await api.post(`/admins/${admin.id}/reset-password`, { new_password: pw });
      toast.success(`Password ${admin.email} direset`);
      setPw(""); setOpen(false); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" data-testid="reset-admin-pw-btn" title="Reset password">
          <KeyRound className="w-4 h-4 text-muted-foreground hover:text-accent" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Reset Password Admin</DialogTitle>
          <DialogDescription>Setel password baru untuk {admin.email}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Password Baru (min. 6 karakter)</Label>
          <Input type="password" data-testid="reset-new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
        </div>
        <DialogFooter><Button onClick={submit} data-testid="reset-pw-submit" className="bg-accent text-white">Reset Password</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EncryptionCard() {
  const [info, setInfo] = useState(null);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/security/encryption"); setInfo(data); }
    catch (e) { /* non-primary */ }
  };
  useEffect(() => { load(); }, []);

  const rotate = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/security/rotate-encryption-key");
      toast.success(`Kunci dirotasi — ${data.rotated} kredensial dienkripsi ulang`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRotating(false); }
  };

  if (!info) return null;
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="encryption-card">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-4 h-4 text-accent" />
        <h3 className="font-display font-semibold">Kunci Enkripsi Database</h3>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 text-sm mb-5">
        <div><p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Kunci Aktif</p><p className="font-mono">{info.key_preview}</p></div>
        <div><p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Kredensial Terenkripsi</p><p className="font-display font-bold text-lg">{info.encrypted_secrets}</p></div>
        <div><p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Rotasi Terakhir</p><p>{info.rotated_at ? new Date(info.rotated_at).toLocaleString("id-ID") : "Belum pernah"}</p></div>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" data-testid="rotate-key-btn" disabled={rotating}>
            <RotateCcw className="w-4 h-4 mr-1.5" /> {rotating ? "Merotasi..." : "Rotasi Kunci Enkripsi"}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotasi Kunci Enkripsi?</AlertDialogTitle>
            <AlertDialogDescription>
              Kunci enkripsi baru akan dibuat dan semua password database tersimpan akan dienkripsi ulang secara otomatis.
              Proses ini aman dan tidak menghilangkan password yang ada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={rotate} data-testid="rotate-key-confirm" className="bg-accent text-white">Rotasi Sekarang</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const INVITE_ROLES = [
  { v: "business_admin", label: "Admin Bisnis" },
  { v: "operator", label: "Operator" },
];
const EMPTY_INVITE = { name: "", email: "", role: "business_admin", business_ids: [] };

function AdminManager({ currentUser }) {
  const [admins, setAdmins] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_INVITE);
  const isSuper = currentUser?.role === "super_admin";

  const load = async () => {
    const { data } = await api.get("/admins");
    setAdmins(data);
  };
  useEffect(() => {
    load();
    api.get("/businesses").then((r) => setBusinesses(r.data)).catch(() => {});
  }, []);

  const toggleBiz = (id) =>
    setForm((f) => ({
      ...f,
      business_ids: f.business_ids.includes(id) ? f.business_ids.filter((x) => x !== id) : [...f.business_ids, id],
    }));

  const create = async () => {
    try {
      const { data } = await api.post("/admins/invite", {
        name: form.name, email: form.email, role: form.role, business_ids: form.business_ids,
      });
      setForm(EMPTY_INVITE);
      setOpen(false);
      await load();
      toast.success(data.email_sent ? "Undangan terkirim ke email admin" : "Admin dibuat (email gagal terkirim)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admins/${id}`);
      await load();
      toast.success("Admin dihapus");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-accent" />
          <h3 className="font-display font-semibold">Kelola Admin</h3>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="add-admin-btn" className="bg-accent hover:bg-accent/90 text-white">
              <Plus className="w-4 h-4 mr-1" /> Tambah Admin
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Undang Admin Baru</DialogTitle>
              <DialogDescription>Kami akan mengirim email berisi tautan untuk membuat password.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Nama</Label>
                <Input data-testid="admin-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Email</Label>
                <Input data-testid="admin-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div className="space-y-1.5">
                <Label>Hak Akses (Privilege)</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVITE_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Bisnis yang Dapat Diakses</Label>
                <div className="space-y-1.5 max-h-40 overflow-y-auto rounded-lg border border-border p-3" data-testid="admin-business-list">
                  {businesses.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox data-testid={`admin-biz-${b.id}`} checked={form.business_ids.includes(b.id)}
                        onCheckedChange={() => toggleBiz(b.id)} />
                      <span className="w-2 h-2 rounded-full" style={{ background: b.config.accent_color }} />
                      {b.name}
                    </label>
                  ))}
                  {businesses.length === 0 && <p className="text-xs text-muted-foreground">Belum ada bisnis.</p>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} data-testid="admin-create-submit"
                disabled={!form.name || !form.email || form.business_ids.length === 0}
                className="bg-accent text-white">Kirim Undangan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2" data-testid="admin-list">
        {admins.map((a) => (
          <div key={a.id} className="flex items-center gap-3 rounded-xl border border-border p-3">
            <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">
              {(a.name || "A")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm truncate">{a.name}
                {a.id === currentUser?.id && <span className="text-xs text-muted-foreground"> (Anda)</span>}
              </p>
              <p className="text-xs text-muted-foreground font-mono truncate">{a.email}</p>
            </div>
            {a.is_primary && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                <Star className="w-3 h-3 mr-1" /> Utama
              </Badge>
            )}
            {a.pending_invite && (
              <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20" data-testid="pending-invite-badge">
                Menunggu undangan
              </Badge>
            )}
            {isSuper && a.id !== currentUser?.id && <ResetPasswordDialog admin={a} />}
            <Button size="icon" variant="ghost" data-testid="delete-admin-btn"
              disabled={a.is_primary || a.id === currentUser?.id} onClick={() => remove(a.id)}>
              <Trash2 className={`w-4 h-4 ${a.is_primary || a.id === currentUser?.id ? "opacity-30" : "text-muted-foreground hover:text-destructive"}`} />
            </Button>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        Setiap admin hanya melihat & mengelola bisnisnya sendiri. Super Admin dapat mereset password admin lain.
      </p>
    </div>
  );
}
export default function Settings() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    if (next.length < 6) { toast.error("Password baru minimal 6 karakter"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password admin berhasil diperbarui");
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4" />
          <h3 className="font-display font-semibold">Akun Admin</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Nama</p><p>{user?.name}</p></div>
          <div><p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Email</p><p className="font-mono">{user?.email}</p></div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-5">
          <ShieldCheck className="w-4 h-4 text-accent" />
          <h3 className="font-display font-semibold">Ubah Password Admin</h3>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Password Saat Ini</Label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="pl-10" required />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Password Baru</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="new-password" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="pl-10" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Konfirmasi Password Baru</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-10" required />
              </div>
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="save-password" className="bg-accent hover:bg-accent/90 text-white">
            {saving ? "Menyimpan..." : "Perbarui Password"}
          </Button>
        </form>
      </div>

      {user?.role === "super_admin" && <AdminManager currentUser={user} />}
      {user?.role === "super_admin" && <EncryptionCard />}
    </div>
  );
}
